/**
 * Temporary screen-scoped design tokens for **Settings → My Profile**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "My Profile" section) 1:1.
 * Every colour, size, radius, spacing and type value the screen needs lives
 * here — components must never inline a literal. Once all screens are
 * re-skinned the shared values move into `src/theme` and this file is removed.
 *
 * Field colours have already made that move: they live in
 * `theme.palette.formField` (see `theme/colors.ts`) and are re-exported through
 * `field` below so the screen and the shared inputs never drift apart.
 */

import {
  button,
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const profileTokens = {
  /** White rounded card chrome shared by all four blocks on the screen. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: "24px 28px",
    /** Gap between the avatar column and the details column. */
    columnGap: "28px",
    /** Newsletter / close-account rows are shorter than the form cards. */
    rowPadding: "18px 28px",
    rowGap: "14px",
  },
  /** Card heading + supporting sentence. */
  heading: {
    color: signer.ink,
    fontSize: "15px",
    fontWeight: 800,
    letterSpacing: "-0.01em",
    lineHeight: "normal",
    gap: "3px",
    /** Gap between the heading block and the fields below it. */
    bodyGap: "16px",
  },
  description: {
    color: signer.text.meta,
    fontSize: "12.5px",
    lineHeight: 1.55,
  },
  avatar: {
    columnWidth: 132,
    size: 96,
    bg: signer.surface.muted,
    border: contentCard.borderMuted,
    color: signer.ink,
    fontSize: "30px",
    fontWeight: 800,
    gap: "10px",
    buttonGap: "6px",
  },
  /** Small outlined "Change" / "Remove" buttons under the avatar. */
  smallButton: {
    height: 30,
    paddingX: "12px",
    radius: "8px",
    bg: white,
    border: formField.border,
    fontSize: "11.5px",
    fontWeight: 700,
    color: signer.ink,
    hoverBorder: signer.ink,
    dangerColor: notification.error.icon,
    dangerHoverBorder: notification.error.icon,
  },
  field: {
    /*
      Field geometry is the app's, not this screen's: 44px on a 10px radius
      with 14px text and a 14px inset, matching Contacts and the shared
      `CustomInput` default. These four Settings screens had drifted to a
      40/9px/13px box of their own, which read as a different control from one
      page to the next. See `global/components/CustomInput/FormField.tokens.ts`.
    */
    rowGap: "14px",
    columnGap: "18px",
    labelGap: "6px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: formField.label,
    labelMutedColor: formField.labelMuted,
    requiredColor: formField.required,
    height: 44,
    radius: "10px",
    border: formField.border,
    bg: formField.bg,
    color: formField.text,
    fontSize: "14px",
    fontWeight: 500,
    /*
      15px, not 14: the design inset is measured inside the field's 1px border,
      but MUI paints that border on an overlaid `fieldset` rather than
      insetting it, so the input needs design padding + border width to land on
      the same pixel. Contacts resolves to the same 15px.
    */
    paddingX: "15px",
    focusBorder: formField.focusBorder,
    placeholder: formField.placeholder,
    disabledBorder: formField.disabledBorder,
    disabledBg: formField.disabledBg,
    disabledColor: formField.disabledText,
    errorFontSize: "10.5px",
  },
  roleChip: {
    color: signer.purple.main,
    bg: signer.surface.muted,
    border: "#E6E1F0",
    fontSize: "12px",
    fontWeight: 700,
    padding: "5px 12px",
    radius: "999px",
    gap: "6px",
    iconSize: 12,
    rowGap: "8px",
    noteFontSize: "11px",
    noteColor: signer.text.faint,
  },
  /**
   * Primary action button. Its chrome now lives in the global `MuiButton`
   * `contained` override, so only the height is re-exported here — the loading
   * skeleton needs it to reserve the same space as the real button.
   */
  primaryButton: {
    height: button.height,
    paddingX: button.paddingX,
    radius: button.radius,
    fontSize: button.fontSize,
  },
  /** Signature / initials preview tiles. */
  signature: {
    rowGap: "16px",
    rowMarginTop: "16px",
    signatureWidth: 340,
    initialsWidth: 220,
    labelGap: "8px",
    labelFontSize: "10.5px",
    labelFontWeight: 700,
    labelLetterSpacing: "0.1em",
    labelColor: signer.text.faint,
    tileHeight: 104,
    tileBg: "#FAF8F3",
    tileBorder: "#EFEBE0",
    tileRadius: "12px",
    tileHoverBorder: signer.orange.main,
    baselineColor: "#D8D2C2",
    baselineWidth: "1.5px",
    baselineInset: "26px",
    baselineBottom: "18px",
    inkColor: signer.ink,
    markMaxHeight: 52,
    markMaxWidth: "62%",
    editSize: 28,
    editOffset: "8px",
    editBg: white,
    editBorder: "#E8E8EF",
    editIconSize: 12,
    editIconColor: signer.ink,
    emptyBorder: "#D8D2C2",
    emptyBorderWidth: "1.5px",
    emptyGap: "4px",
    emptyPlusFontSize: "20px",
    emptyLabelFontSize: "12px",
    emptyLabelFontWeight: 600,
    emptyColor: signer.text.faint,
  },
  /** Newsletter / close-account row heading. */
  row: {
    titleColor: signer.ink,
    titleFontSize: "13.5px",
    titleFontWeight: 700,
    gap: "2px",
  },
  /** Shared app toggle chrome — see `theme/colors.ts` `switchControl`. */
  toggle: {
    width: switchControl.width,
    height: switchControl.height,
    knobSize: switchControl.knobSize,
    knobTop: `${switchControl.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.knobInset + switchControl.knobTravel}px)`,
    trackOff: switchControl.trackOff,
    trackOn: switchControl.trackOn,
    knobBg: switchControl.knob,
    knobShadow: switchControl.knobShadow,
    transition: switchControl.trackTransition,
    knobTransition: switchControl.knobTransition,
  },
  danger: {
    cardBorder: "#F2DBDB",
    buttonHeight: 36,
    buttonPaddingX: "16px",
    buttonRadius: "9px",
    buttonBorder: "#E8C9C9",
    buttonColor: notification.error.icon,
    buttonFontSize: "12.5px",
    buttonFontWeight: 700,
    buttonHoverBg: signer.danger.tint,
    buttonHoverBorder: notification.error.icon,
  },
  footer: {
    fontSize: "11.5px",
    color: signer.text.faint,
    lineHeight: 1.6,
    paddingX: "4px",
    // signer.orange.main is 4.29:1 on the page fill, so rest and hover both use
    // darker shades (6.4:1 and ~11:1, WCAG 1.4.3), and the link is underlined
    // so it does not rely on color (1.4.1).
    linkColor: signer.orange.dark,
    linkHoverColor: inkfree.darkest,
  },
} as const;

/**
 * Phone (`xs`) and tablet (`sm`) values for **Settings → My Profile**, traced
 * from `Settings - Mobile & Tablet.dc.html`.
 *
 * Only what actually differs from the desktop tokens above lives here; anything
 * omitted is inherited at `md`, so the desktop screen is untouched. `sm` / `std`
 * inside `phone` are the reference's 375 and 390 columns.
 */
export const profileResponsiveTokens = {
  phone: {
    card: {
      padding: { sm: "18px 16px", std: "20px 18px" },
      /** The phone card is one flex column, so this gap spaces every block. */
      gap: { sm: "14px", std: "15px" },
      /** Close-account card runs tighter than the form cards. */
      rowPadding: { sm: "16px", std: "18px" },
      rowGap: "8px",
    },
    heading: { fontSize: { sm: "14.5px", std: "15px" } },
    description: { fontSize: { sm: "12px", std: "12.5px" } },
    avatar: {
      size: { sm: 64, std: 70 },
      fontSize: { sm: "22px", std: "24px" },
      /** Avatar and its buttons sit side by side, not stacked. */
      rowGap: { sm: "14px", std: "15px" },
      buttonGap: "8px",
    },
    smallButton: {
      height: { sm: 32, std: 34 },
      paddingX: { sm: "14px", std: "15px" },
      fontSize: { sm: "12px", std: "12.5px" },
    },
    field: {
      labelFontSize: { sm: "11.5px", std: "12px" },
      height: { sm: 42, std: 44 },
      fontSize: { sm: "13.5px", std: "14px" },
    },
    roleChip: {
      fontSize: { sm: "11.5px", std: "12px" },
      padding: { sm: "4px 11px", std: "5px 12px" },
    },
    primaryButton: {
      height: { sm: 42, std: 44 },
      paddingX: { sm: "20px", std: "22px" },
      fontSize: { sm: "13px", std: "13.5px" },
    },
    signature: {
      tileHeight: { sm: 96, std: 104 },
      rowMarginTop: "10px",
    },
    row: { titleFontSize: { sm: "13.5px", std: "14px" } },
    danger: {
      buttonHeight: { sm: 38, std: 40 },
      buttonPaddingX: { sm: "16px", std: "18px" },
      buttonFontSize: { sm: "12.5px", std: "13px" },
    },
  },

  tablet: {
    card: {
      padding: "22px 24px",
      columnGap: "22px",
      rowPadding: "18px 24px",
      rowGap: "16px",
    },
    heading: { fontSize: "14.5px", bodyGap: "14px" },
    description: { fontSize: "12px" },
    avatar: {
      columnWidth: 100,
      size: 80,
      fontSize: "26px",
      gap: "9px",
    },
    smallButton: {
      height: 28,
      paddingX: "10px",
      radius: "7px",
      fontSize: "10.5px",
    },
    field: {
      rowGap: "12px",
      columnGap: "16px",
      labelFontSize: "11px",
      height: 38,
      radius: "8px",
      paddingX: "11px",
      fontSize: "13px",
    },
    roleChip: { fontSize: "11.5px", padding: "6px 12px" },
    primaryButton: {
      height: 38,
      paddingX: "18px",
      radius: "8px",
      fontSize: "12.5px",
    },
    signature: {
      signatureWidth: 320,
      /**
       * The reference only draws the signature tile (320px). The real card also
       * carries initials, so the second tile is trimmed to whatever is left of
       * the 528px card interior once the reference width and the 16px gap are
       * taken — both still sit on one row, as they do on desktop.
       */
      initialsWidth: 190,
      tileHeight: 100,
      rowMarginTop: "14px",
    },
    row: { titleFontSize: "13.5px" },
    danger: {
      buttonHeight: 36,
      buttonPaddingX: "16px",
      buttonFontSize: "12.5px",
    },
  },
} as const;

export default profileTokens;
